import React, { Children, ElementType, FC, PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@context/ModalsContext'

import { SearchField, SearchFieldProps } from '@ui/SearchField/SearchField'
import { Heading6 } from '@ui/Typography/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Button, IconButton } from '@ui/components/Button'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { Tabs, TabsProps } from '@ui/components/Tabs'
import { Tooltip, TooltipProps } from '@ui/components/Tooltip/Tooltip'
import { TextColorType } from '@ui/design-system/colors/colors'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'
import { ArrowsDownUpIcon } from '@ui/icons/ArrowsDownUp.icon'
import { DummyIcon } from '@ui/icons/Dummy.icon'
import { FilterIcon } from '@ui/icons/Filter.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { IconFCType } from '@ui/icons/types'

import { PageQuery } from '@utils/ApiClient'
import { useWindowResize } from '@utils/domUtils'
import { isFunction } from '@utils/isFunction'

import { ExportHubPayload } from '@model/ExportHub'
import { NameDto } from '@model/NameDto'

import { Box } from '@shared/components/Box'
import { InformationModal } from '@shared/modals/Information.modal'
import { SortModal } from '@shared/modals/table/Sort.modal'

import { TourTarget } from '../Tourtip/TourTarget'
import { DownloadDropdown } from './DownloadDropdown'

import styles from './PageSection.module.scss'

type InfoData = TooltipProps | (() => void)

interface BasePageSectionProps {
  icon?: ElementType
  title: string | React.ReactNode
  renderMessage?: () => React.ReactNode | null
  onGoBack?: () => void
  documentFile?: {
    handleGenerateExcel: () => void
    handleGeneratePDF: () => void
    handleGenerateCSV: () => void
  }
  openExportModal?: () => void
  exportData?: Omit<ExportHubPayload, 'format' | 'mode'>
  searchField?: SearchFieldProps
  renderActions?: {
    icon?:
      | IconFCType
      | {
          icon: IconFCType
          color: TextColorType
        }
    onClick?: () => void
    tooltip?: TooltipProps
    title?: string
    condition?: boolean
    disabled?: boolean
    dropdown?: {
      state: [boolean, React.Dispatch<React.SetStateAction<boolean>>]
      options: {
        label: string
        onClick?: () => void
        icon:
          | IconFCType
          | {
              icon: IconFCType
              color: TextColorType
            }
      }[]
    }
    tourTarget?: string
  }[]
  tabData?: TabsProps
  id?: string
  infoData?: InfoData
  hasMinHeight?: boolean
  onFilterClick?: () => void
  isFilterButtonDisabled?: boolean
  infoModalData?: {
    title: string
    body: React.ReactNode
    buttonLabel?: string
  }
}

type NoSortProps = {
  sortOptions?: never
  pageQuery?: never
  setPageQuery?: never
}

type WithSortProps = {
  sortOptions: NameDto<string>[]
  pageQuery?: PageQuery
  setPageQuery?: (pageQuery: PageQuery) => void
}

export type PageSectionProps = BasePageSectionProps & (NoSortProps | WithSortProps)

export const PageSection: FC<PropsWithChildren<PageSectionProps>> = (props) => {
  const {
    children,
    renderActions,
    tabData,
    hasMinHeight,
    infoData,
    documentFile,
    icon: Icon,
    title,
    onFilterClick,
    id,
    pageQuery,
    setPageQuery,
    sortOptions,
    isFilterButtonDisabled = false,
    infoModalData,
    openExportModal,
  } = props

  const { t } = useTranslation()
  const { onGoBack, renderMessage, searchField } = props
  const isMobile = useWindowResize()
  const infoModal = useModal(InformationModal, {
    title: infoModalData?.title,
    description: infoModalData?.body,
    secondaryLabel: infoModalData?.buttonLabel,
  })

  const sortModal = useModal(SortModal, {
    options: sortOptions,
    onConfirm: (option, sortBy, sortName) => {
      setPageQuery?.({
        ...pageQuery,
        sort: option,
        sortOrder: sortBy,
        sortName,
      })
    },
  })

  const handleDownload = (fileType?: string) => {
    switch (fileType) {
      case 'pdf':
        documentFile?.handleGeneratePDF()
        break
      case 'csv':
        documentFile?.handleGenerateCSV()
        break
      case 'excel':
        documentFile?.handleGenerateExcel()
        break
      default:
        break
    }
  }

  const hasActions =
    (renderActions?.length ?? 0) > 0 ||
    documentFile ||
    searchField ||
    Children.count(children) > 0 ||
    onFilterClick

  return (
    <Box flex direction='column' gap='x8'>
      <Box
        flex
        align='center'
        justify='space-between'
        gap='x16'
        fullWidth
        className={styles.pageSection}
        id={id}
      >
        <Box
          flex
          direction={!!tabData && isMobile ? 'column' : undefined}
          align={!!tabData && isMobile ? 'initial' : 'stretch'}
          gap='x16'
          justify='space-between'
          fullWidth
          wrap
          minHeight={hasMinHeight && isMobile ? 48 : undefined}
          pt={isMobile ? 'x8' : undefined}
        >
          <Box flex gap='x8' pb='x8' align='center' justify='flex-start'>
            {onGoBack ? (
              <IconButton.Secondary size='md' icon={ArrowIcon.Left} onClick={onGoBack} />
            ) : (
              !!Icon && <Icon color='text-high-em' />
            )}
            <Box flex align='center' gap='x8' wrap flexItem='1'>
              <Heading6 medium>{title}</Heading6>
              {renderMessage?.()}
            </Box>
            {infoData &&
              (isFunction(infoData) ? (
                <Tooltip {...infoData}>
                  <ActionIcon onClick={infoData} icon={InfoIcon} size='x20' />
                </Tooltip>
              ) : (
                <Tooltip {...infoData}>
                  <ActionIcon icon={InfoIcon} size='x20' />
                </Tooltip>
              ))}
            {infoModalData && (
              <Box flex align='center' justify='center' onClick={() => infoModal.open()}>
                <ActionIcon icon={InfoIcon} size='x20' />
              </Box>
            )}
          </Box>
          {(hasActions || tabData) && (
            <Box
              flex
              align='stretch'
              justify={isMobile ? 'space-between' : 'flex-end'}
              gap='x24'
              maxWidth='100%'
            >
              {tabData && (
                <Box flex overflowX='hidden' overflowY='hidden'>
                  <Tabs {...tabData} />
                </Box>
              )}
              {hasActions && (
                <Box flex align='center' pb='x8' gap='x8'>
                  {searchField && (searchField.renderResult || !isMobile) && (
                    <SearchField {...searchField} />
                  )}
                  {renderActions
                    ?.filter(({ condition }) => condition !== false)
                    .map(
                      ({ title, tooltip, onClick, icon: Icon, disabled, dropdown, tourTarget }) => (
                        <Tooltip {...tooltip} key={title}>
                          {dropdown?.options ? (
                            <Popover
                              direction='topRight'
                              distance='0.5rem'
                              isOpen={dropdown.state[0]}
                              setIsOpen={dropdown.state[1]}
                              content={
                                <PopoverCard>
                                  <PopoverList>
                                    {dropdown.options.map(({ label, icon, onClick }) => (
                                      <ListItem
                                        key={label}
                                        onClick={onClick}
                                        startSlot={{ icon: icon }}
                                      >
                                        {label}
                                      </ListItem>
                                    ))}
                                  </PopoverList>
                                </PopoverCard>
                              }
                            >
                              <IconButton.Secondary
                                size='md'
                                icon={
                                  typeof Icon === 'function'
                                    ? { icon: Icon, color: 'text-high-em' }
                                    : {
                                        icon: Icon?.icon || DummyIcon,
                                        color: Icon?.color || 'text-high-em',
                                      }
                                }
                                onClick={() => dropdown.state[1](true)}
                              />
                            </Popover>
                          ) : (!title || isMobile) && Icon ? (
                            <TourTarget target={tourTarget}>
                              <IconButton.Secondary
                                icon={typeof Icon === 'function' ? Icon : Icon.icon || DummyIcon}
                                disabled={disabled}
                                onClick={onClick}
                              />
                            </TourTarget>
                          ) : (
                            <TourTarget target={tourTarget}>
                              <Button.Secondary
                                size='md'
                                iconLeft={
                                  typeof Icon === 'function'
                                    ? {
                                        icon: Icon,
                                        color: disabled ? 'text-low-em' : 'text-med-em',
                                      }
                                    : Icon?.icon
                                    ? {
                                        icon: Icon?.icon || DummyIcon,
                                        color: Icon?.color || 'text-high-em',
                                      }
                                    : undefined
                                }
                                disabled={disabled}
                                onClick={onClick}
                              >
                                {title}
                              </Button.Secondary>
                            </TourTarget>
                          )}
                        </Tooltip>
                      )
                    )}
                  {(sortOptions?.length ?? 0) > 0 && isMobile && (
                    <IconButton.Secondary
                      size='md'
                      icon={{ icon: ArrowsDownUpIcon, color: 'text-med-em' }}
                      onClick={() => sortModal.open()}
                    />
                  )}
                  {onFilterClick &&
                    (isMobile ? (
                      <IconButton.Secondary
                        size='md'
                        icon={{
                          icon: FilterIcon,
                          color: isFilterButtonDisabled ? 'text-low-em' : 'text-med-em',
                        }}
                        disabled={isFilterButtonDisabled}
                        onClick={onFilterClick}
                      />
                    ) : (
                      <Button.Secondary
                        size='md'
                        iconLeft={{
                          icon: FilterIcon,
                          color: isFilterButtonDisabled ? 'text-low-em' : 'text-med-em',
                        }}
                        disabled={isFilterButtonDisabled}
                        onClick={onFilterClick}
                      >
                        {t('Filters')}
                      </Button.Secondary>
                    ))}

                  {documentFile && !openExportModal ? (
                    <DownloadDropdown onDownload={handleDownload} />
                  ) : openExportModal ? (
                    <IconButton.Secondary
                      size='md'
                      icon={{ icon: ArrowCircleIcon.Down, color: 'text-high-em' }}
                      onClick={openExportModal}
                    />
                  ) : null}

                  {children}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
      {!!searchField && !searchField.renderResult && isMobile && <SearchField {...searchField} />}
    </Box>
  )
}
